import { isApiError, userMessage } from './api/errors';

/** What we could learn about the scan in the browser (null for a PDF or an unreadable file). */
export interface ScanInfo {
  width: number;
  height: number;
}

export interface ScanFailure {
  title: string;
  /** Likely causes, most likely first. */
  reasons: string[];
  /** Whether "Try again" with the same file can help (only for passing problems). */
  retrySame: boolean;
}

/** Below this many pixels on the long side the MRZ letters are too small to read. */
export const MIN_LONG_SIDE_PX = 800;

/** Read an image's size in the browser; null for a PDF or a file the browser can't decode. */
export function readScanInfo(file: File): Promise<ScanInfo | null> {
  if (!file.type.startsWith('image/')) return Promise.resolve(null);
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

/** Problems visible from the file itself, whatever the server said. */
function fileReasons(file: File | null, info: ScanInfo | null): string[] {
  const out: string[] = [];
  if (info) {
    const long = Math.max(info.width, info.height);
    if (long < MIN_LONG_SIDE_PX) {
      out.push(`The image is small (${info.width} × ${info.height} px), so the letters are too tiny to read. Use a photo at least ${MIN_LONG_SIDE_PX} px wide.`);
    }
    if (info.height > info.width * 1.6) {
      out.push('The image is tall and narrow: the passport may be photographed sideways or with lots of background. Fill the frame with the open photo page.');
    }
  }
  if (file && file.size < 60 * 1024 && file.type !== 'application/pdf') {
    out.push('The file is very small, so it is probably heavily compressed or blurry. Send the original photo, not a screenshot or chat-compressed copy.');
  }
  return out;
}

/** Why a passport scan failed, as a list the user can act on. */
export function describeScanFailure(err: unknown, file: File | null, info: ScanInfo | null): ScanFailure {
  const fromFile = fileReasons(file, info);

  if (isApiError(err, 422)) {
    const detail = typeof err.detail === 'string' ? err.detail : '';
    if (/could not be read as an image/i.test(detail)) {
      return {
        title: "The file couldn't be opened as an image",
        reasons: [
          'The file may be damaged or only partly uploaded.',
          'A PDF may be empty, password-protected, or contain no picture of the passport.',
          'The file extension may not match its content (e.g. a renamed HEIC photo).',
        ],
        retrySame: false,
      };
    }
    return {
      title: "We couldn't find the passport's machine-readable lines",
      reasons: [
        ...fromFile,
        'The two lines of <<< at the bottom of the photo page are cut off, covered (a finger, the cover, a sticker) or out of the frame.',
        'The photo is blurry or out of focus.',
        'Light reflects off the page (glare) over the bottom lines.',
        'The page is photographed at an angle, curved, or upside down.',
        'This is not the photo page of the passport.',
      ],
      retrySame: false,
    };
  }
  if (isApiError(err, 429)) {
    return {
      title: 'Scan limit reached',
      reasons: ['You can scan at most 20 passports per hour. Try again later, or enter the details by hand.'],
      retrySame: false,
    };
  }
  if (isApiError(err, 503)) {
    return {
      title: 'Passport reading is unavailable right now',
      reasons: ['The reading service is restarting or not set up. Try again in a minute, or enter the details by hand.'],
      retrySame: true,
    };
  }
  if (isApiError(err, 504)) {
    return {
      title: 'Reading took too long',
      reasons: [
        ...fromFile,
        'The image is very large or has a lot around the passport. Crop it to the photo page.',
        'The server is busy. Try again in a moment.',
      ],
      retrySame: true,
    };
  }
  return {
    title: "Couldn't read the passport",
    reasons: [userMessage(err, 'Something went wrong while reading the passport.'), ...fromFile],
    retrySame: true,
  };
}
